import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { ArrowLeft, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { RootState } from '../store'
import { isAdminUser } from '../utils/admin'
import adminDbService, {
  type AdminDbColumnSchema,
  type AdminDbTableSchema,
} from '../services/adminDbService'

const PAGE_SIZE = 50

function formatCell(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function isJsonColumn(col: AdminDbColumnSchema): boolean {
  return col.type === 'json' || col.type === 'jsonb'
}

function listColumns(schema: AdminDbTableSchema): AdminDbColumnSchema[] {
  const pk = new Set(schema.primaryKey)
  const preferred = schema.columns.filter(
    (c) => !isJsonColumn(c) && (pk.has(c.name) || c.name.endsWith('Id') || c.type === 'string'),
  )
  const rest = schema.columns.filter((c) => !isJsonColumn(c) && !preferred.includes(c))
  return [...preferred, ...rest].slice(0, 7)
}

function formColumns(schema: AdminDbColumnSchema[], mode: 'create' | 'edit'): AdminDbColumnSchema[] {
  return schema.filter((col) => {
    if (col.generated && col.primary) return false
    if (mode === 'create' && col.generated) return false
    return true
  })
}

function rowId(row: Record<string, unknown>, schema: AdminDbTableSchema): string {
  const pk = schema.primaryKey[0] ?? 'id'
  return String(row[pk] ?? '')
}

function valueToFormField(value: unknown, col: AdminDbColumnSchema): string {
  if (value == null) return ''
  if (isJsonColumn(col)) return JSON.stringify(value, null, 2)
  if (col.type === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

function formFieldToPayload(value: string, col: AdminDbColumnSchema): unknown {
  if (value === '' && col.nullable) return null
  if (isJsonColumn(col)) return value
  if (col.type === 'boolean') return value === 'true' || value === '1'
  if (col.type === 'number' || col.type === 'integer' || col.type === 'float') {
    const n = Number(value)
    return Number.isNaN(n) ? value : n
  }
  return value
}

const AdminDbTable: React.FC<{ fixedTable?: string; backTo?: string }> = ({
  fixedTable,
  backTo,
}) => {
  const { table: tableParam = '' } = useParams<{ table: string }>()
  const table = fixedTable ?? tableParam
  const listBackTo = backTo ?? '/admin/db'
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)

  const [schema, setSchema] = useState<AdminDbTableSchema | null>(null)
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isAdminUser(user)) navigate('/dashboard', { replace: true })
  }, [user, navigate])

  const loadSchema = useCallback(async () => {
    if (!table) return
    const s = await adminDbService.getSchema(table)
    setSchema(s)
  }, [table])

  const fetchPage = useCallback(
    async (pageNum: number, append: boolean) => {
      if (!table) return
      if (append) setLoadingMore(true)
      else setLoading(true)
      try {
        const res = await adminDbService.listRows(table, { page: pageNum, limit: PAGE_SIZE })
        setRows((prev) => (append ? [...prev, ...res.rows] : res.rows))
        setTotal(res.total)
        setPage(pageNum)
        setHasMore(pageNum < res.totalPages)
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to load rows')
        if (!append) setRows([])
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [table],
  )

  useEffect(() => {
    if (!isAdminUser(user) || !table) return
    void loadSchema()
    void fetchPage(1, false)
  }, [user, table, loadSchema, fetchPage])

  const displayCols = useMemo(() => (schema ? listColumns(schema) : []), [schema])

  const openCreate = () => {
    if (!schema) return
    setEditing(null)
    const initial: Record<string, string> = {}
    for (const col of formColumns(schema.columns, 'create')) {
      if (col.type === 'boolean') initial[col.name] = 'false'
      else initial[col.name] = ''
    }
    setForm(initial)
    setModalOpen(true)
  }

  const openEdit = (row: Record<string, unknown>) => {
    if (!schema) return
    setEditing(row)
    const initial: Record<string, string> = {}
    for (const col of formColumns(schema.columns, 'edit')) {
      initial[col.name] = valueToFormField(row[col.name], col)
    }
    setForm(initial)
    setModalOpen(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!schema || !table) return
    setSaving(true)
    try {
      const cols = formColumns(schema.columns, editing ? 'edit' : 'create')
      const payload: Record<string, unknown> = {}
      for (const col of cols) {
        if (editing && col.primary) continue
        payload[col.name] = formFieldToPayload(form[col.name] ?? '', col)
      }
      if (editing) {
        await adminDbService.updateRow(table, rowId(editing, schema), payload)
        toast.success('Row updated')
      } else {
        await adminDbService.createRow(table, payload)
        toast.success('Row created')
      }
      setModalOpen(false)
      await fetchPage(1, false)
    } catch (err: any) {
      const msg = err?.response?.data?.message
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (row: Record<string, unknown>) => {
    if (!schema || !table) return
    const id = rowId(row, schema)
    if (!id || !window.confirm(`Delete row ${id}?`)) return
    try {
      await adminDbService.deleteRow(table, id)
      toast.success('Row deleted')
      await fetchPage(1, false)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Delete failed')
    }
  }

  if (!isAdminUser(user)) return null

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 sm:pt-20 lg:pt-24 pb-8 sm:pb-12">
        <div className="container mx-auto px-3 xs:px-4 sm:px-6 max-w-6xl">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Link to={listBackTo} className="p-2 rounded-xl hover:bg-muted/40 text-muted-foreground">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-display text-2xl font-bold text-foreground">{table}</h1>
                <p className="text-sm text-muted-foreground">
                  {total} rows · {schema?.entity ?? '…'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Add row
            </button>
          </div>

          {loading ? (
            <div className="py-16 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Loading…
            </div>
          ) : (
            <>
              <div className="rounded-2xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 text-left">
                      <tr>
                        {displayCols.map((c) => (
                          <th key={c.name} className="px-3 py-2 font-medium whitespace-nowrap">
                            {c.name}
                          </th>
                        ))}
                        <th className="px-3 py-2 font-medium" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={displayCols.length + 1}
                            className="px-3 py-8 text-center text-muted-foreground"
                          >
                            No rows
                          </td>
                        </tr>
                      ) : (
                        rows.map((row) => {
                          const id = schema ? rowId(row, schema) : ''
                          return (
                            <tr key={id} className="border-t border-border hover:bg-muted/20">
                              {displayCols.map((c) => (
                                <td key={c.name} className="px-3 py-2 max-w-[200px] truncate">
                                  {formatCell(row[c.name])}
                                </td>
                              ))}
                              <td className="px-3 py-2 whitespace-nowrap text-right">
                                <button
                                  type="button"
                                  onClick={() => openEdit(row)}
                                  className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground"
                                  title="Edit"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleDelete(row)}
                                  className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              {hasMore && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    disabled={loadingMore}
                    onClick={() => void fetchPage(page + 1, true)}
                    className="px-6 py-2.5 rounded-xl border border-border bg-muted/30 text-sm font-medium disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading…' : `Load more (${rows.length} of ${total})`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {modalOpen && schema && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="w-full max-w-lg glass-strong rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-display font-bold text-foreground mb-4">
              {editing ? 'Edit row' : 'Add row'}
            </h2>
            <form onSubmit={handleSave} className="space-y-3">
              {formColumns(schema.columns, editing ? 'edit' : 'create').map((col) => (
                <div key={col.name}>
                  <label className="block text-sm font-medium mb-1">
                    {col.name}
                    <span className="text-muted-foreground font-normal ml-1">({col.type})</span>
                  </label>
                  {col.type === 'boolean' ? (
                    <select
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                      value={form[col.name] ?? 'false'}
                      onChange={(e) => setForm((f) => ({ ...f, [col.name]: e.target.value }))}
                    >
                      <option value="false">false</option>
                      <option value="true">true</option>
                    </select>
                  ) : isJsonColumn(col) ? (
                    <textarea
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background font-mono text-xs min-h-[100px]"
                      value={form[col.name] ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, [col.name]: e.target.value }))}
                    />
                  ) : (
                    <input
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                      value={form[col.name] ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, [col.name]: e.target.value }))}
                      readOnly={!!editing && col.primary}
                    />
                  )}
                </div>
              ))}
              <button
                type="submit"
                disabled={saving}
                className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-60"
              >
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminDbTable
